import React, { useCallback, useEffect, useState } from 'react';
import {
  Users,
  CreditCard,
  Copy,
  Link2,
  Loader2,
  TrendingUp,
  IndianRupee,
} from 'lucide-react';
import { getInfluencerDashboard } from '../api/influencer.api';
import {
  buildInfluencerShareUrl,
  buildInfluencerSignupUrl,
} from '../utils/influencerAttribution';

const InfluencerDashboardView = ({ showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to view your influencer dashboard.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getInfluencerDashboard(token);
      setData(res.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (msg, type = 'info') => {
    if (showNotification) showNotification(msg, type);
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(`${label} copied to clipboard`, 'success');
    } catch {
      notify('Could not copy — please copy manually', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto mt-12 p-6 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center">
        {error}
      </div>
    );
  }

  const promoCode = data?.promoCode || '';
  const stats = data?.stats || {};
  const shareUrl = buildInfluencerShareUrl(promoCode);
  const signupUrl = buildInfluencerSignupUrl(promoCode);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Influencer Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Track signups and payments from your referral link and promo code.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-violet-100 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Link2 size={20} className="text-violet-600" />
            Your promo code & links
          </h2>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="font-mono text-xl font-bold text-violet-700 bg-violet-50 px-4 py-2 rounded-lg">
              {promoCode || '—'}
            </span>
            <span className="text-sm text-gray-500">
              {data?.discountPercent ?? 10}% off for customers at checkout
            </span>
          </div>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 font-mono"
                aria-label="Share link"
              />
              <button
                type="button"
                onClick={() => copyText(shareUrl, 'Share link')}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700"
              >
                <Copy size={16} />
                Copy link
              </button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                readOnly
                value={signupUrl}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 font-mono"
                aria-label="Signup link with coupon"
              />
              <button
                type="button"
                onClick={() => copyText(signupUrl, 'Signup link')}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-violet-200 text-violet-700 rounded-lg text-sm font-medium hover:bg-violet-50"
              >
                <Copy size={16} />
                Copy signup URL
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Share links like <code className="bg-gray-100 px-1 rounded">/ref/{promoCode}</code> or add{' '}
            <code className="bg-gray-100 px-1 rounded">?coupon={promoCode}</code> to any page.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Signups"
            value={stats.signupCount ?? 0}
            color="blue"
          />
          <StatCard
            icon={CreditCard}
            label="Payments"
            value={stats.paymentCount ?? 0}
            color="violet"
          />
          <StatCard
            icon={IndianRupee}
            label="Revenue (INR)"
            value={`₹${(stats.totalRevenueInr ?? 0).toLocaleString('en-IN')}`}
            color="emerald"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <RecentList
            title="Recent signups"
            emptyText="No signups yet — share your link!"
            items={(stats.recentSignups || []).map((row) => ({
              id: row._id,
              primary: row.userId?.name || 'User',
              secondary: row.userId?.phone || row.userId?.email || '',
              date: row.createdAt,
            }))}
          />
          <RecentList
            title="Recent payments"
            emptyText="No payments yet."
            items={(stats.recentPayments || []).map((row) => ({
              id: row._id,
              primary: row.userId?.name || 'Customer',
              secondary: `₹${(row.amountInr || 0).toLocaleString('en-IN')} · ${row.paymentPurpose || 'payment'}`,
              date: row.createdAt,
            }))}
          />
        </div>
      </div>
    </div>
  );
};

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: 'from-blue-500 to-cyan-500',
    violet: 'from-violet-500 to-purple-500',
    emerald: 'from-emerald-500 to-teal-500',
  };
  return (
    <div className="bg-white rounded-2xl shadow border border-gray-100 p-5">
      <div className={`inline-flex p-2 rounded-lg bg-gradient-to-r ${colors[color] || colors.violet} mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}

function RecentList({ title, items, emptyText }) {
  return (
    <div className="bg-white rounded-2xl shadow border border-gray-100 p-5">
      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <TrendingUp size={18} className="text-violet-500" />
        {title}
      </h3>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400">{emptyText}</p>
      ) : (
        <ul className="space-y-2 max-h-64 overflow-y-auto">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex justify-between items-start text-sm border-b border-gray-50 pb-2 last:border-0"
            >
              <div>
                <p className="font-medium text-gray-800">{item.primary}</p>
                {item.secondary && <p className="text-gray-500 text-xs">{item.secondary}</p>}
              </div>
              {item.date && (
                <span className="text-xs text-gray-400 shrink-0 ml-2">
                  {new Date(item.date).toLocaleDateString()}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default InfluencerDashboardView;
